<form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col max-h-[82vh]">
  <div class="flex flex-col gap-4 p-5 overflow-y-auto">
    <!-- Route Insertion Context Pill -->
    <div class="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-surface border border-border">
      <lucide-icon name="Route" size="16" class="text-primary shrink-0"></lucide-icon>
      <app-text size="xs" weight="medium" class="text-foreground">
        {{ insertionContextText() }}
      </app-text>
    </div>

    <!-- Step 1: Stop Purpose Selector -->
    <app-form-card title="1. What happens at this stop?" icon="MapPinPlus">
      <app-radio-card-group
        [options]="purposeRadioOptions()"
        layout="grid"
        [gridCols]="2"
        [ngModel]="stopPurpose()"
        (ngModelChange)="selectPurpose($event)"
        [ngModelOptions]="{ standalone: true }"
      ></app-radio-card-group>
    </app-form-card>

    <!-- Step 2: Location & Appointment -->
    <app-form-card title="2. Where & when is this stop?" icon="MapPin">
      @if (hasSavedYards()) {
        <app-form-field label="Location Source">
          <app-button-group
            [options]="locationTypeOptions"
            [ngModel]="locationType()"
            (ngModelChange)="onLocationTypeChange($event)"
            [ngModelOptions]="{ standalone: true }"
          ></app-button-group>
        </app-form-field>
      }

      @if (locationType() === 'saved' && hasSavedYards()) {
        <app-form-field label="Saved Yard / Terminal" [required]="true">
          <app-select
            [options]="yardOptions()"
            [ngModel]="selectedYardId()"
            (ngModelChange)="onYardSelect($event)"
            [ngModelOptions]="{ standalone: true }"
            placeholder="Select a saved yard or terminal"
          ></app-select>
        </app-form-field>

        @if (selectedYard(); as yard) {
          <div class="p-3.5 rounded-xl border border-border bg-surface-high flex flex-col gap-1.5">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <lucide-icon
                  [name]="yard.isTerminal ? 'Building' : 'Warehouse'"
                  [size]="16"
                  class="text-primary"
                ></lucide-icon>
                <app-heading level="h4" class="font-semibold text-foreground text-sm">
                  {{ yard.name }}
                </app-heading>
              </div>
              <app-badge
                [title]="yard.isTerminal ? 'Terminal' : 'Drop Yard'"
                variant="primary"
                size="xs"
                badgeStyle="outline"
              ></app-badge>
            </div>
            <app-text size="xs" color="muted">
              {{ yard.address.street ? yard.address.street + ', ' : '' }}{{ formatCityStateZip(yard.address) }}
            </app-text>
          </div>
        }
      } @else {
        <app-form-field label="Facility / Location Name" [required]="true">
          <app-input
            formControlName="companyName"
            placeholder="e.g. Kingman Drop Yard or Receiver DC"
          ></app-input>
        </app-form-field>

        <app-address-form formControlName="address"></app-address-form>
      }

      <!-- Optional Appointment Toggle -->
      <div class="flex flex-col gap-3 pt-2 border-t border-border">
        <app-checkbox
          label="Set scheduled appointment date & time (stop-local)"
          size="sm"
          [ngModel]="hasAppointment()"
          (ngModelChange)="hasAppointment.set($event)"
          [ngModelOptions]="{ standalone: true }"
        ></app-checkbox>

        @if (hasAppointment()) {
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <app-form-field label="Appointment Date" [required]="true">
              <app-date-picker
                formControlName="appointmentDate"
                placeholder="Select date"
              ></app-date-picker>
            </app-form-field>

            <app-form-field
              label="Appointment Time (Stop-Local)"
              hint="Facility local timezone"
            >
              <app-time-picker
                formControlName="appointmentTime"
                placeholder="08:00"
              ></app-time-picker>
            </app-form-field>
          </div>
        }
      </div>
    </app-form-card>

    <!-- Step 3: Progressive Disclosure — Driver / Equipment Leg Split -->
    @if (isIntermediateInsert()) {
      <app-form-card
        [title]="stopPurpose() === 'handoff' ? '3. Assign Oncoming Leg (Stop #' + (insertAfterStopNumber + 1) + ' onward)' : '3. Driver & Equipment Continuity'"
        icon="ArrowRightLeft"
      >
        @if (stopPurpose() !== 'handoff') {
          <app-checkbox
            label="Switch driver or equipment from this stop onward (Split into new leg)"
            [ngModel]="splitHere()"
            (ngModelChange)="onToggleSplitHere($event)"
            [ngModelOptions]="{ standalone: true }"
          ></app-checkbox>
        }

        @if (splitHere()) {
          <!-- Outgoing Leg Context Strip -->
          <div class="flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg bg-surface-high border border-border text-xs">
            <div class="flex items-center gap-1.5 min-w-0">
              <app-text size="xs" color="muted">Outgoing Leg:</app-text>
              <app-text size="xs" weight="semibold" class="truncate">
                {{ outgoingDriverDisplay() }}
              </app-text>
            </div>
            <app-text size="xs" color="muted" class="shrink-0">
              {{ outgoingEquipmentDisplay() }}
            </app-text>
          </div>

          <app-form-field
            label="Swap Type"
            hint="Equipment not being swapped carries forward automatically from the outgoing leg"
          >
            <app-button-group
              [options]="exchangeTypeOptions"
              [ngModel]="exchangeType()"
              (ngModelChange)="onExchangeTypeChange($event)"
              [ngModelOptions]="{ standalone: true }"
            ></app-button-group>
          </app-form-field>

          @if (exchangeType() !== 'equipment_only') {
            <div class="flex flex-col gap-3">
              <div class="flex items-center justify-between gap-2">
                <app-text size="xs" color="muted">Who takes over from this stop onward?</app-text>
                <app-checkbox
                  label="Team leg (add co-driver)"
                  size="sm"
                  [ngModel]="isTeamOncoming()"
                  (ngModelChange)="onToggleTeam($event)"
                  [ngModelOptions]="{ standalone: true }"
                ></app-checkbox>
              </div>

              <app-form-field label="Oncoming Primary Driver" [required]="true">
                <app-select
                  formControlName="oncomingDriverId"
                  [options]="driverOptions()"
                  placeholder="Select oncoming driver"
                ></app-select>
              </app-form-field>

              <app-driver-credential-warning
                [driver]="selectedOncomingDriver()"
                [loadDate]="loadEndDate()"
              ></app-driver-credential-warning>

              @if (driverConflicts().length > 0) {
                <div class="p-2.5 rounded-lg bg-surface-high border border-warning flex items-start gap-2">
                  <lucide-icon name="AlertTriangle" size="14" class="text-warning shrink-0 mt-0.5"></lucide-icon>
                  <app-text size="xs" class="text-warning">
                    Driver is also assigned on Load #{{ driverConflicts()[0].loadExternalId || driverConflicts()[0].loadId }}.
                  </app-text>
                </div>
              }

              @if (isTeamOncoming()) {
                <app-form-field label="Co-Driver (Team Driver 2)">
                  <app-select
                    formControlName="oncomingDriver2Id"
                    [options]="driverOptions()"
                    placeholder="Select team co-driver"
                  ></app-select>
                </app-form-field>
                <app-driver-credential-warning
                  [driver]="selectedOncomingDriver2()"
                  [loadDate]="loadEndDate()"
                ></app-driver-credential-warning>
              }
            </div>
          }

          @if (exchangeType() !== 'driver_only') {
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-border">
              <app-form-field label="Oncoming Truck" [required]="true">
                <app-select
                  formControlName="oncomingTruckId"
                  [options]="truckOptions()"
                  placeholder="Select truck"
                ></app-select>
              </app-form-field>

              <app-form-field label="Oncoming Trailer">
                <app-select
                  formControlName="oncomingTrailerId"
                  [options]="trailerOptions()"
                  placeholder="Select trailer"
                ></app-select>
              </app-form-field>
            </div>

            @if (truckConflicts().length > 0) {
              <div class="p-2.5 rounded-lg bg-surface-high border border-warning flex items-start gap-2">
                <lucide-icon name="AlertTriangle" size="14" class="text-warning shrink-0 mt-0.5"></lucide-icon>
                <app-text size="xs" class="text-warning">
                  Truck has an overlapping assignment on Load #{{ truckConflicts()[0].loadExternalId || truckConflicts()[0].loadId }}.
                </app-text>
              </div>
            }
          }

          <!-- Commercial Boundary Facility Attribution -->
          @if (isCommercialPurpose()) {
            <div class="pt-2 border-t border-border">
              <app-form-field
                label="Stop Dock Attribution"
                hint="Which driver handles loading/unloading here (earns stop pay, detention & lumper reimbursement)?"
              >
                <app-button-group
                  [options]="servicedByOptions"
                  [ngModel]="servicedBy()"
                  (ngModelChange)="servicedBy.set($event)"
                  [ngModelOptions]="{ standalone: true }"
                ></app-button-group>
              </app-form-field>
            </div>
          }

          <!-- Relay Reason Chips & Handoff Notes -->
          <div class="flex flex-col gap-4 pt-2 border-t border-border">
            <app-form-field
              label="Relay Reason"
              hint="Select why the driver or equipment is changing at this stop"
            >
              <app-select-chip
                [options]="reasonChipOptions"
                [ngModel]="selectedReasonChip()"
                (ngModelChange)="onSelectReasonChip($event)"
                [ngModelOptions]="{ standalone: true }"
              ></app-select-chip>
            </app-form-field>

            @if (selectedReasonChip() === 'Other') {
              <app-form-field label="Specify Reason" [required]="true">
                <app-input
                  formControlName="customExchangeReason"
                  placeholder="Enter custom reason for driver / equipment swap"
                ></app-input>
              </app-form-field>
            }

            <app-form-field
              label="Handoff Instructions (Optional)"
              hint="Shown to both outgoing and oncoming drivers in the Haulverse Driver app"
            >
              <app-textarea
                formControlName="exchangeNotes"
                [rows]="2"
                placeholder="e.g. Drop loaded trailer in Bay 4, verify seal # intact"
              ></app-textarea>
            </app-form-field>
          </div>
        }
      </app-form-card>
    }

    <!-- Step 4: Facility Stop Instructions & Internal Notes -->
    <app-form-card
      title="Stop Instructions & Internal Notes (Optional)"
      icon="NotebookPen"
      [collapsible]="true"
      [defaultCollapsed]="stopPurpose() === 'handoff'"
    >
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <app-form-field
          label="Driver Stop Instructions"
          hint="Gate code, check-in bay, receiving contact"
        >
          <app-textarea
            formControlName="instructions"
            [rows]="2"
            placeholder="Gate code, check-in bay..."
          ></app-textarea>
        </app-form-field>

        <app-form-field
          label="Internal Dispatcher Notes"
          hint="Internal team reference"
        >
          <app-textarea
            formControlName="notes"
            [rows]="2"
            placeholder="Dispatcher notes..."
          ></app-textarea>
        </app-form-field>
      </div>
    </app-form-card>
  </div>

  <div class="flex items-center justify-end gap-3 p-4 border-t border-border bg-surface-low rounded-b-xl">
    <app-button variant="ghost" type="button" (click)="onCancel()" [disabled]="isLoading()">
      Cancel
    </app-button>
    <app-button variant="primary" type="submit" [loading]="isLoading()" icon="Check">
      {{ isIntermediateInsert() && splitHere() ? 'Add Stop & Split Route' : 'Add Stop' }}
    </app-button>
  </div>
</form>
